/**
 * Bitcoin Script Type Module
 * 
 * This module defines all supported Bitcoin script types and locking mechanisms.
 * Script types determine how bitcoins are locked and what conditions must be met
 * to spend them. This includes legacy, SegWit, and Taproot script types.
 */

// ============================================================================
// 1. ENUM: Source of Truth for Script Types
// ============================================================================

/**
 * All supported Bitcoin script types.
 * 
 * Legacy Scripts:
 * - P2PKH: Pay-to-Public-Key-Hash (original Bitcoin addresses starting with 1)
 * - P2SH_MULTISIG: Pay-to-Script-Hash for multisig (addresses starting with 3)
 * 
 * SegWit Scripts:
 * - P2SH_P2WPKH: Nested SegWit (SegWit wrapped in P2SH, addresses starting with 3)
 * - P2WPKH: Native SegWit (bech32 addresses starting with bc1q)
 * - P2WSH_P2SH: Wrapped Witness Script Hash
 * - P2WSH: Native Witness Script Hash (for complex scripts)
 * 
 * Taproot Scripts:
 * - P2TR: Pay-to-Taproot (bech32m addresses starting with bc1p)
 */
export enum ScriptType {
    P2PKH = "p2pkh",
    P2SH_P2WPKH = "p2sh-p2wpkh",
    P2WPKH = "p2wpkh",
    P2TR = "p2tr",
    P2SH_MULTISIG = "p2sh-multisig",
    P2WSH_P2SH = "p2wsh-p2sh",
    P2WSH = "p2wsh",
}

// ============================================================================
// 2. TYPE: Union of Script Type Values (lowercase)
// ============================================================================

/**
 * Union type of all script type values (lowercase strings as used in protocol).
 * Equivalent to:
 * "p2pkh" | "p2sh-p2wpkh" | "p2wpkh" | "p2tr" | "p2sh-multisig" | "p2wsh-p2sh" | "p2wsh"
 */
export type ScriptTypeValue = `${ScriptType}`;

// ============================================================================
// 3. TYPE: Union of Script Type Keys (uppercase)
// ============================================================================

/**
 * Union type of all script type keys (uppercase enum keys).
 * Equivalent to:
 * "P2PKH" | "P2SH_P2WPKH" | "P2WPKH" | "P2TR" | "P2SH_MULTISIG" | "P2WSH_P2SH" | "P2WSH"
 */
export type ScriptTypeUppercaseKey = keyof typeof ScriptType;

// ============================================================================
// 4. MAP: Script Type Key → Script Type Value
// ============================================================================

/**
 * Maps uppercase script type keys to lowercase script type values.
 * Example: { P2PKH: "p2pkh", P2WPKH: "p2wpkh", ... }
 */
export const ScriptTypeMap = { ...ScriptType } satisfies {
    [K in ScriptTypeUppercaseKey]: ScriptType;
};

// ============================================================================
// 5. REVERSE MAP: Script Type Value → Script Type Key
// ============================================================================

/**
 * Maps lowercase script type values to uppercase script type keys.
 * This is useful for getting the label/key from a protocol value.
 * Example: { "p2wpkh": "P2WPKH", "p2wsh": "P2WSH", ... }
 */
export const ScriptTypeReverseMap = Object.fromEntries(
    Object.entries(ScriptType).map(([upper, lower]) => [lower, upper])
) as Record<ScriptTypeValue, ScriptTypeUppercaseKey>;

// ============================================================================
// 6. UTILITY FUNCTIONS
// ============================================================================

/**
 * Converts a script type key (uppercase) to its corresponding value (lowercase).
 * @param t - Script type key (e.g., "P2WSH_P2SH")
 * @returns Script type value (e.g., "p2wsh-p2sh")
 * @example scriptTypeToValue("P2WPKH") // returns "p2wpkh"
 */
export function scriptTypeToValue(t: ScriptTypeUppercaseKey): ScriptTypeValue {
    return ScriptTypeMap[t];
}

/**
 * Converts a script type value (lowercase) to its corresponding key (uppercase).
 * @param v - Script type value (e.g., "p2wsh")
 * @returns Script type key (e.g., "P2WSH")
 * @example scriptTypeFromValue("p2wpkh") // returns "P2WPKH"
 */
export function scriptTypeFromValue(v: ScriptTypeValue): ScriptTypeUppercaseKey {
    return ScriptTypeReverseMap[v];
}
